import {AddressFormValue} from "../../data/models/subscription-order.model";
import {isDigital} from "./offer-utils";

/**
 * Minimal-Shape des Angebots für die Digital-/Print-Weiche. Bewusst strukturell und nicht als
 * Offer typisiert: OrderState.offer ist untypisiert, und Tests sollen kein komplettes Offer bauen müssen.
 */
export type OfferTypeSource = { offerType?: { ePaper: boolean, paidContent: boolean } };

/**
 * Eine Adresse ohne Straße gilt als nicht angegeben — dieselbe Konvention wie in
 * SubscriptionOrderConverter.isAddressEmpty. Deckt alle Leer-Varianten ab: fehlendes Objekt,
 * fehlende address-Gruppe, null nach reset() und Leerstring aus der Vorbefüllung.
 */
export function isAddressFormValueEmpty(address: AddressFormValue): boolean {
  return !address?.address?.street;
}

/**
 * Entscheidet, welche Adresse nach einer Online-Zahlung (PayPal/Google Pay) im Bestell-State steht.
 *
 * - Eine bereits erfasste Adresse wird NIE durch die Adresse des Zahlungsanbieters überschrieben.
 * - Bei Printabos wird die Anbieter-Adresse grundsätzlich nicht übernommen: Dort kommt der
 *   Adress-Step vor dem Zahlungs-Step, der Nutzer gibt die Lieferadresse also ohnehin selbst ein.
 * - Nur bei digitalen Abos (ePaper/PaidContent) gibt es keinen Adress-Step; dort ist die
 *   Anbieter-Adresse die einzige Quelle, solange der Nutzer selbst keine Adresse hat.
 *
 * Fail-safe: Ist das Angebot (oder sein offerType) unbekannt, wird wie bei Print NICHT übernommen.
 * Eine falsch übernommene Fremdadresse führt zu einer falsch ausgelieferten Zeitung, eine fehlende
 * Adresse fällt dagegen in der Validierung auf. Bitte nicht "optimieren".
 */
export function mergeExternalAddress(
  existingAddress: AddressFormValue,
  externalAddress: AddressFormValue,
  offer: OfferTypeSource
): AddressFormValue {
  // Der Anbieter hat gar keine Adresse geliefert.
  if (!externalAddress) {
    return existingAddress;
  }

  // Der Nutzer hat bereits eine Adresse eingegeben oder mitgebracht.
  if (!isAddressFormValueEmpty(existingAddress)) {
    return existingAddress;
  }

  // Print oder unbekanntes Angebot: keine Übernahme.
  if (!isDigital(offer?.offerType)) {
    return existingAddress;
  }

  return externalAddress;
}

/**
 * Ist die Anschrift vollständig, also für eine Bestellung brauchbar?
 *
 * Geprüft werden dieselben Felder, die der Adress-Step als Pflichtfelder führt. Namens- und
 * Hausnummernfelder kommen im Formularwert in zwei Schreibweisen vor (firstName/firstname,
 * houseNo/houseno) - beide gelten.
 */
export function isAddressFormValueComplete(address: AddressFormValue): boolean {
  const person: any = address?.person ?? {};
  const anschrift: any = address?.address ?? {};

  const filled = (value: unknown): boolean => (value ?? '').toString().trim() !== '';
  const key = (value: any): unknown => (typeof value === 'string' ? value : value?.key);

  return filled(key(person.salutation))
    && filled(person.firstName ?? person.firstname)
    && filled(person.lastName ?? person.lastname)
    && filled(anschrift.street)
    && filled(anschrift.houseNo ?? anschrift.houseno)
    && filled(anschrift.zipcode)
    && filled(anschrift.city)
    && filled(key(anschrift.country));
}
